import { useEffect } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { LiveRegions } from '@/components/LiveRegions/LiveRegions'
import { useCopy } from '@/i18n/useCopy'
import { LOCALES } from '@/i18n/locales'
import { setHapticsEnabled } from '@/lib/haptics'
import { ConversationScreen } from '@/screens/Conversation/ConversationScreen'
import { DiagnosticsScreen } from '@/screens/Diagnostics/DiagnosticsScreen'
import { ProfileScreen } from '@/screens/Profile/ProfileScreen'
import { SettingsScreen } from '@/screens/Settings/SettingsScreen'
import { TermsScreen } from '@/screens/Terms/TermsScreen'
import { useSettingsStore } from '@/store/settingsStore'

export default function App() {
  useDocumentChrome()

  return (
    <BrowserRouter>
      {/*
        The three screen-reader live regions are mounted once, at the root, and
        stay mounted for the life of the app. A live region that is created
        with its message already inside it is frequently never announced, and
        one that unmounts between routes takes the announcement queue with it.
      */}
      <LiveRegions />

      <Routes>
        <Route path="/" element={<ConversationScreen />} />
        <Route path="/session" element={<ConversationScreen />} />
        <Route path="/profile" element={<ProfileScreen />} />
        <Route path="/settings" element={<SettingsScreen />} />
        <Route path="/diagnostics" element={<DiagnosticsScreen />} />
        <Route path="/terms" element={<TermsScreen />} />
        <Route path="*" element={<ConversationScreen />} />
      </Routes>
    </BrowserRouter>
  )
}

/**
 * Keeps the document element in step with the settings: language, direction
 * and colour scheme.
 *
 * `lang` is not cosmetic here. It is what tells a screen reader which voice to
 * read the page with, and getting it wrong means Hausa read aloud with English
 * phonetics, which is precisely the failure this product exists to end.
 */
function useDocumentChrome() {
  const copy = useCopy()
  const locale = useSettingsStore((store) => store.locale)
  const appearance = useSettingsStore((store) => store.appearance)
  const haptics = useSettingsStore((store) => store.haptics)

  useEffect(() => {
    const descriptor = LOCALES[locale]
    document.documentElement.lang = descriptor.bcp47
    document.documentElement.dir = descriptor.direction
  }, [locale])

  useEffect(() => {
    if (appearance === 'system') document.documentElement.removeAttribute('data-scheme')
    else document.documentElement.setAttribute('data-scheme', appearance)
  }, [appearance])

  useEffect(() => {
    setHapticsEnabled(haptics)
  }, [haptics])

  useEffect(() => {
    document.title = `${copy.ui.appName} · ${copy.ui.tagline}`
  }, [copy])
}
